import anime from "animejs";
import GeoLocation from "@core/geolocation";
import { $, $$, rect } from "@utils/dom";
import { on, off } from "@utils/listener";
import { INVIEW_ENTER } from "@scroll/constants";
import ResizeOrientation from "@utils/resize";
import Viewport from "@utils/viewport";

const DEFAULT_VIEW = 'grid';

class MealArchives {
  constructor(el, emitter) {
    this.el = el;
    this.emitter = emitter;

    this._view = null;
    this._currentGroup = null;
    this._currentDay = null;
    this._currentTocItem = null;
    this._scrollOffset = null;

    this.viewBtns = [...$$('.meal-archives__viewBtn', this.el)];
    this.mealPreviews = [...$$('.meal-archives__mealPreview', this.el)];
    this.mealWrap = $('.meal-archives__mealWrap', this.el);

    this.allergens = [...$$('.meal-preview__allergens', this.el)];

    this.tocLinks = [...$$('.meal-archives__tocLink', this.el)];
    this.tocBtn = $('.meal-archives__tocBtn', this.el);
    this.tocBtnLabel = $('.meal-archives__tocBtnLabel', this.el);
    this.tocWrap = $('.meal-archives__tocWrap', this.el);
    this.toc = $('.meal-archives__toc', this.el);

    this.openingHours = $('.meal-archives__openingHours', this.el);
    this.restaurant = $('.meal-archives__restaurant', this.el);

    this._onBtnViewClick = this._onBtnViewClick.bind(this);
    this._onScrollSpy = this._onScrollSpy.bind(this);
    this._onResize = this._onResize.bind(this);
    this._onLinkClick = this._onLinkClick.bind(this);
    this._onTocToggle = this._onTocToggle.bind(this);
    this._onFavoriteRestaurantResponse = this._onFavoriteRestaurantResponse.bind(this);
    this._onMenuURLResponse = this._onMenuURLResponse.bind(this);
  }

  init() {
    this._onResize();
    this._getInitialView();
    this._initHours();
    GeoLocation.request();

    if (this.allergens) this.tooltips = this.allergens.forEach(el => new Tooltip(el, this.emitter));
    if (this.openingHours) this.interval = setInterval(this._initHours, 60000);

    this._bindEvents();
  }
  destroy() {
    this._unbindEvents();

    if (this.interval) clearInterval(this.interval);

    if (this.tooltips) this.tooltips.forEach(tooltip => tooltip.destroy());

    this.el = null;
    this.emitter = null;
    this.interval = null;

    this.allergens =  null;
    this.tooltips = null;

    this._view = null;
    this._currentGroup = null;
    this._currentDay = null;
    this._currentTocItem = null;
    this._scrollOffset = null;

    this.viewBtns = null;
    this.mealPreviews = null;
    this.mealWrap = null;

    this.tocLinks = null;
    this.tocBtn = null;
    this.tocWrap = null;
    this.toc = null;

    this.openingHours = null;

    this._onBtnViewClick = null;
    this._onScrollSpy = null;
    this._onResize = null;
    this._onLinkClick = null;
    this._onTocToggle = null;
    this._onFavoriteRestaurantResponse = null;
    this._onMenuURLResponse = null;
  }

  _bindEvents() {
    if (this.viewBtns) on(this.viewBtns, 'click', this._onBtnViewClick);
    ResizeOrientation.add(this._onResize);

    if (this.tocLinks) {
      on(this.tocLinks, 'click', this._onLinkClick);
      if (this.emitter) this.emitter.on('SiteScroll.MealArchivesSpy', this._onScrollSpy);
    }

    if (this.emitter) this.emitter.on('FavoriteRestaurant.response', this._onFavoriteRestaurantResponse);

    if (this.tocBtn && this.tocWrap) {
      on(this.tocBtn, 'click', this._onTocToggle);
    }
  }
  _unbindEvents() {
    if (this.viewBtns) off(this.viewBtns, 'click', this._onBtnViewClick);
    ResizeOrientation.remove(this._onResize);
    if (this.tocLinks) {
      on(this.tocLinks, 'click', this._onLinkClick);
      if (this.emitter) this.emitter.on('SiteScroll.MealArchivesSpy', this._onScrollSpy);
    }

    if (this.emitter) this.emitter.off('FavoriteRestaurant.response', this._onFavoriteRestaurantResponse);

    if (this.tocBtn && this.toc) {
      off(this.tocBtn, 'click', this._onTocToggle);
    }
  }

  _initHours(){
    if (!this.openingHours) return;

    const time = new Date();
    const currentDay = time.getDay();

    if (currentDay == this._currentDay) return;

    this._currentDay = currentDay;

    const schedule = JSON.parse(this.openingHours.dataset.schedule);
    const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

    const day = schedule[days[currentDay]];

    this.openingHours.innerHTML = `${day.openingHour}-${day.closingHour}`;
  }

  _onResize(){
    const spacerHeight = parseInt(window.getComputedStyle(this.el).getPropertyValue('--site-header-spacer-height'));
    const stickyOffset = parseInt(window.getComputedStyle(this.el).getPropertyValue('--sticky-offset'));
    this._scrollOffset = spacerHeight + stickyOffset;

    if (this._currentTocItem) this.toc.style.setProperty('--toc-selected-top', `${rect(this._currentTocItem).top - rect(this.toc).top}px`);

  }

  _onBtnViewClick(event){
    const view = event.currentTarget.dataset.view;

    this.view = view;

    if (this.mealWrap) this.emitter.emit('SiteScroll.scrollTo', this.mealWrap, {offset: (this._scrollOffset * -1)});
  }

  _onLinkClick(event){
    if (event) event.preventDefault();
    const groupId = event.currentTarget.dataset.groupId;
    const target = $(`#${groupId}`, this.el);

    if (this.tocWrap) this.tocWrap.classList.remove('--js-open');
    if (this.emitter) this.emitter.emit('SiteScroll.scrollTo', target, {offset: (this._scrollOffset * -1)});
  }

  _getInitialView(){
    if (localStorage.getItem('meal-preview-view')) {
      this.view = localStorage.getItem('meal-preview-view');
    } else {
      this.view = DEFAULT_VIEW;
    }
  }

  _onScrollSpy(direction, obj) {
    const inView = direction === INVIEW_ENTER;

    // toggle RAF
    if(inView) {
      this._selectTocLink(obj.target.id, obj.target.dataset.groupName);
    }
  }

  _selectTocLink(group, name=null){
    if (!this.tocLinks) return;

    this._currentGroup = group;

    this.tocLinks.forEach(item => {
      if (item.dataset.groupId == this._currentGroup) {
        item.setAttribute('aria-current', true);

        this.toc.style.setProperty('--toc-selected-top', `${rect(item).top - rect(this.toc).top}px`);

        this._currentTocItem = item;
      } else {
        item.removeAttribute('aria-current');
      }
    });

    if (name && this.tocBtnLabel) this.tocBtnLabel.innerHTML = name;
  }


  _onTocToggle(){
    this.tocWrap.classList.toggle('--js-open');
  }

  _onFavoriteRestaurantResponse(response) {
    if(!this.restaurant) return;

    // stop here when --has-restaurant class
    if(this.restaurant.classList.contains('--has-restaurant')) return;

    // get restaurant menuURL
    const { menuURL } = response;

    // bail early if no menuURL
    if( !menuURL ) return;

    // fetch menuURL page
    fetch(menuURL)
      .then(res => res.text())
      .then(this._onMenuURLResponse);

  }

  _onMenuURLResponse(response) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(response, 'text/html');

    // find .meal-archives container
    const restaurant = doc.querySelector('.meal-archives__restaurant');

    // bail early if no restaurant found
    if(!restaurant) return;

    // fade-out current restaurant element
    anime({
      targets: this.restaurant,
      opacity: [1, 0],
      easing: 'linear',
      duration: 250,
      complete: () => {
        this.restaurant.innerHTML = restaurant.innerHTML;
        this.restaurant.classList.add('--has-restaurant');

        // update opening hours
        this.openingHours = $('.meal-archives__openingHours', this.el);
        this._initHours();

        // fade-in updated restaurant element
        anime({
          targets: this.restaurant,
          opacity: 1,
          easing: 'linear',
          duration: 250
        });
      }
    });
  }

  // getter/setter

  set view(value){
    if (this.el.classList.contains(`--view-${this._view}`)) {
      this.el.classList.replace(`--view-${this._view}`, `--view-${value}`);
    } else {
      this.el.classList.add(`--view-${value}`);
    }

    this._view = value;
    localStorage.setItem('meal-preview-view', this._view);

    this.viewBtns.forEach(btn => {
      if (btn.dataset.view == this._view) {
        btn.setAttribute('aria-current', true);
      } else {
        btn.removeAttribute('aria-current');
      }
    });

    this.mealPreviews.forEach(preview => {
      preview.setAttribute('data-preview-variation', this._view);
    });

    this.emitter.emit('SiteScroll.update');
  }

  get view(){
    return this._view;
  }

}

class Tooltip {
  constructor(el, emitter) {
    this.el = el;
    this.emitter = emitter;

    this.trigger = $('.meal-preview__allergensBtn', this.el);
    this.tooltip = $('.meal-preview__allergensTooltip', this.el);

    this._opened = false;
    this._direction = null;

    this._toggle = this._toggle.bind(this);
    this._onClickOutside = this._onClickOutside.bind(this);

    this.init();
  }

  init(){
    this._bindEvents();
  }

  _bindEvents(){
    if (this.trigger) on(this.trigger, 'click', this._toggle);
  }

  _unbindEvents(){
    if (this.trigger) off(this.trigger, 'click', this._toggle);

    off(window, "click", this._onClickOutside);
  }

  destroy(){
    this._unbindEvents();

    this.el = null;
    this.emitter = null;

    this.trigger = null;
    this.tooltip = null;

    this._opened = null;
    this._direction = null;

    this._toggle = null;
    this._onClickOutside = null;
  }

  _toggle(){
    if (this.opened) {
      this._close();
    } else {
      this._open();
    }
  }

  _open(){
    this._setDirection();

    this.opened = true;

    off(window, "click", this._onClickOutside);
    setTimeout(() => on(window, "click", this._onClickOutside), 1);
  }

  _close(){
    this.opened = false;
  }

  _onClickOutside(){
    off(window, "click", this._onClickOutside);

    this.opened = false;
  }

  _setDirection(){
    const tooltipRect = rect(this.tooltip);
    const triggerRect = rect(this.trigger);
    let direction = 'bottom';

    const bottom = triggerRect.bottom + 15 + tooltipRect.height;

    if (bottom > Viewport.height) {
      direction = 'top';
    }

    if (this._direction) {
      this.tooltip.classList.replace(`--direction-${this._direction}`,`--direction-${direction}`);
    }  else {
      this.tooltip.classList.add(`--direction-${direction}`);
    }

    this._direction = direction;
  }

  set opened(value){
    this._opened = value;

    this.trigger.setAttribute('aria-expanded', this._opened);
    this.tooltip.setAttribute('aria-hidden', !this._opened);

  }

  get opened(){
    return this._opened;
  }


}

export default MealArchives;
